import { create } from 'zustand'
import { BackgroundWaveColor, colorArray } from '@/components/timer/background/waveColors'

interface WaveState {
  // Wave state
  waveValue: number
  colorIndex: number

  // Actions
  setWaveValue: (value: number) => void
  setColorIndex: (index: number) => void
  setWaveValueByProgress: (current: number, total: number) => void
  updateWaveByTimerProgress: (timeLeft: number, totalDuration: number) => void

  // Getters
  getColorTheme: () => BackgroundWaveColor

  // Utility functions
  pulseWave: (duration?: number) => void
  celebrateCompletion: () => void
  resetWave: () => void
  nextColorTheme: () => void
  previousColorTheme: () => void
}

export const useWaveStore = create<WaveState>((set, get) => ({
  // Initial state
  waveValue: 50,
  colorIndex: 0,

  // Actions
  setWaveValue: (value) => set({
    waveValue: Math.min(Math.max(value, 0), 100)
  }),

  setColorIndex: (index) => set({
    colorIndex: Math.min(Math.max(index, 0), colorArray.length - 1)
  }),

  setWaveValueByProgress: (current, total) => {
    if (total <= 0) return

    // Calculate progress percentage (0-100)
    const progress = (current / total) * 100

    // Set wave value based on progress
    set({ waveValue: Math.min(Math.max(progress, 0), 100) })
  },

  // Update wave based on timer completion percentage
  updateWaveByTimerProgress: (timeLeft, totalDuration) => {
    if (totalDuration <= 0) return

    // Map completion percentage to wave value (100 - completion so wave goes down as timer progresses)
    const waveValue = (timeLeft / totalDuration) * 100

    // Set wave value with bounds checking
    set({ waveValue: Math.min(Math.max(waveValue, 0), 100) })
  },

  // Getters
  getColorTheme: () => {
    const { colorIndex } = get()
    return colorArray[colorIndex]
  },

  // Utility functions
  pulseWave: (duration = 1000) => {
    const { waveValue } = get()
    const originalValue = waveValue

    // Pulse up
    set({ waveValue: Math.min(waveValue + 20, 100) })

    // Return to original value after duration
    setTimeout(() => {
      set({ waveValue: originalValue })
    }, duration)
  },

  // Special effect for timer completion
  celebrateCompletion: () => {
    const { colorIndex } = get()

    // Pulse the wave
    get().pulseWave(800)

    // Cycle through colors
    const colorCycle = setInterval(() => {
      get().nextColorTheme()
    }, 300)

    // Stop after 2 seconds and return to original color
    setTimeout(() => {
      clearInterval(colorCycle)
      set({ colorIndex })
    }, 2000)
  },

  resetWave: () => set({ waveValue: 50 }),

  nextColorTheme: () => {
    const { colorIndex } = get()
    const nextIndex = (colorIndex + 1) % colorArray.length
    set({ colorIndex: nextIndex })
  },

  previousColorTheme: () => {
    const { colorIndex } = get()
    const prevIndex = (colorIndex - 1 + colorArray.length) % colorArray.length
    set({ colorIndex: prevIndex })
  }
}))